import React from 'react';
import { useWatch } from 'react-hook-form';
import { Typography } from '@packages/ui/primitives';
import type { FormEachNode } from '../../types/layout';
import { FormRowExcludedValuesProvider } from '../../context/FormRowContext';
import { useFormEngineForm, useFormEngineState } from '../../context/FormEngineContext';
import { useFormEachArray } from '../../hooks/useFormEachArray';
import { useArrayEffectProcessor } from '../../processors/arrayEffectProcessor';
import { resolveAddButtonPosition } from '../../utils/resolveAddButtonPosition';
import { buildExcludedSiblingValues } from './each/buildExcludedSiblingValues';
import { FormEachAddButton } from './each/FormEachAddButton';
import { FormEachRemoveButton } from './each/FormEachRemoveButton';
import { FormEachResetButton } from './each/FormEachResetButton';
import { FormEachItemFields } from './each/FormEachItemFields';

interface FormEachDefaultRendererProps {
  node: FormEachNode;
}

export const FormEachDefaultRenderer = React.memo(function FormEachDefaultRenderer({
  node,
}: FormEachDefaultRendererProps): React.ReactElement {
  const { form, hydrationEpochRef } = useFormEngineForm();
  const { isReadOnly, meta, getOptionProp } = useFormEngineState();
  const arrayValue = useWatch({ control: form.control, name: node.name as never }) as unknown as unknown[] | undefined;

  useArrayEffectProcessor({
    form: form as never,
    arrayEffects: node.arrayEffects,
    arrayName: node.name,
    meta,
    getOptionProp,
    hydrationEpochRef,
  });

  const {
    fields,
    handleAdd,
    handleRemove,
    showAdd,
    canRemoveRow,
    allRowValues,
    siblingExcludeFields,
  } = useFormEachArray(node);

  const columns = typeof node.columns === 'number' || typeof node.columns === 'string'
    ? node.columns
    : (node.columns ?? 2);

  const addPosition = resolveAddButtonPosition(node);
  const addControl = showAdd && !isReadOnly ? <FormEachAddButton node={node} onClick={handleAdd} /> : null;
  const isArrayEmpty = !Array.isArray(arrayValue) || arrayValue.length === 0;
  const showEmptyPlaceholder = isArrayEmpty && node.emptyState != null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: isReadOnly ? '10px' : '16px' }}>
      {addPosition === 'top-right' && addControl != null && (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>{addControl}</div>
      )}

      {showEmptyPlaceholder && node.emptyState != null && (
        <div style={{ paddingLeft: '30px' }} data-form-each-empty={node.name}>
          <Typography variant='body' color='var(--ui-text, #000)'>
            {node.emptyState.title}
          </Typography>
          {node.emptyState.description != null && (
            <Typography variant='body-xs' color='var(--ui-text-muted)'>
              {node.emptyState.description}
            </Typography>
          )}
        </div>
      )}

      {fields.map((item, index) => {
        const excludedValues = buildExcludedSiblingValues(allRowValues, index, siblingExcludeFields);
        const showRemove = canRemoveRow() && !isReadOnly;

        return (
          <FormRowExcludedValuesProvider key={item.id} value={excludedValues}>
            <FormEachItemFields
              node={node}
              index={index}
              total={fields.length}
              columns={columns}
              itemClassName={node.itemClassName}
            />
            {(showRemove || node.resetButton != null) && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                {node.resetButton != null && <FormEachResetButton node={node} index={index} />}
                {showRemove && <FormEachRemoveButton node={node} index={index} onRemove={handleRemove} />}
              </div>
            )}
          </FormRowExcludedValuesProvider>
        );
      })}

      {addPosition !== 'top-right' && addControl}
    </div>
  );
});
